import { useCallback, useState } from 'react';

import { StyleSheet } from 'react-native';

import { CmsQuestRequirement } from 'components/quests/cms-hooks/types';
import { IQuest } from 'components/quests/loyalty-quest/types';
import { Tier } from 'components/quests/loyalty-quest/utils';
import { Box } from 'components/ui';
import { LoyaltyQuestStepStatus } from 'generated/graphql-gateway';
import { ILoyaltyQuestsUiDetailFragment } from 'generated/sanity-graphql';
import { spacing } from 'styles/constants';

import { TierCard } from '../tier-card';

interface ITieredStepsProps {
  quest: IQuest;
  tiers: Tier[];
  // @TODO: Add proper type for cmsTiers. Sourced from
  // `cmsQuestDetail.questRules.tiers`; each entry's `steps` aligns 1:1 with
  // the runtime `tiers[i].steps` we render.
  cmsTiers: readonly any[];
  cmsQuestRequirementById: CmsQuestRequirement;
  cmsQuestDetailUI: ILoyaltyQuestsUiDetailFragment;
  onCloseDrawer: () => void;
}

export const TieredSteps = ({
  tiers,
  quest,
  cmsTiers,
  cmsQuestRequirementById,
  cmsQuestDetailUI,
  onCloseDrawer,
}: ITieredStepsProps) => {
  const firstUncompletedTierIndex = tiers.findIndex(
    tier => tier.status === LoyaltyQuestStepStatus.NOT_COMPLETED
  );

  // Default: expand only the first uncompleted tier (or all if every tier is done)
  const [collapsedMap, setCollapsedMap] = useState<Record<number, boolean>>(() =>
    Object.fromEntries(
      tiers.map((_, i) => [
        i,
        firstUncompletedTierIndex === -1 ? false : i !== firstUncompletedTierIndex,
      ])
    )
  );

  const toggleTier = useCallback((index: number) => {
    setCollapsedMap(prev => ({ ...prev, [index]: !prev[index] }));
  }, []);

  return (
    <Box style={styles.container}>
      {tiers.map((tier, tierIndex) => (
        <TierCard
          key={tierIndex}
          tier={tier}
          quest={quest}
          cmsSteps={cmsTiers[tierIndex]?.steps ?? []}
          cmsQuestRequirementById={cmsQuestRequirementById}
          cmsQuestDetailUI={cmsQuestDetailUI}
          onCloseDrawer={onCloseDrawer}
          isFirstTier={tierIndex === 0}
          isCollapsed={collapsedMap[tierIndex] ?? false}
          onToggle={() => toggleTier(tierIndex)}
          areStepsLocked={
            // When every tier is completed, findIndex returns -1 because no
            // NOT_COMPLETED tier exists. Without this guard the comparison
            // `tierIndex <= -1` would be false for all tiers, incorrectly
            // marking completed progress as locked.
            !(firstUncompletedTierIndex === -1 || tierIndex <= firstUncompletedTierIndex)
          }
        />
      ))}
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: spacing.$4,
  },
});
